import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import PrintIcon from '@mui/icons-material/Print';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  serverTimestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import LoginPage from '../Members/LoginPage';

const LOAD_TEST_TAG = 'load-test';

const FIRST_NAMES = [
  'Liam',
  'Olivia',
  'Noah',
  'Emma',
  'Oliver',
  'Ava',
  'Elijah',
  'Sophia',
  'James',
  'Isabella',
  'Benjamin',
  'Mia',
  'Lucas',
  'Amelia',
  'Mason',
  'Harper',
  'Ethan',
  'Evelyn',
  'Alexander',
  'Luna',
];

const LAST_NAMES = [
  'Smith',
  'Johnson',
  'Williams',
  'Brown',
  'Jones',
  'Garcia',
  'Miller',
  'Davis',
  'Rodriguez',
  'Martinez',
  'Hernandez',
  'Lopez',
  'Gonzalez',
  'Wilson',
  'Anderson',
  'Thomas',
  'Taylor',
  'Moore',
  'Jackson',
  'Martin',
];

type JobStatus = 'pending' | 'processing' | 'printed' | 'error';

interface QueueChild {
  id: string;
  childFirstName: string;
  childLastName: string;
  age: number;
  pickupPin: string;
  parentName: string;
}

interface QueueEntry {
  id: string;
  children: QueueChild[];
  status: JobStatus;
  requestedAt?: { toDate: () => Date } | null;
  printedAt?: { toDate: () => Date } | null;
  claimedAt?: { toDate: () => Date } | null;
  error?: string;
}

const randomOf = <T,>(arr: T[]): T => arr[Math.floor(Math.random() * arr.length)];

const randomPin = () => Math.floor(1000 + Math.random() * 9000).toString();

const randomChild = (sharedLastName?: string): QueueChild => ({
  id: `load-test-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  childFirstName: randomOf(FIRST_NAMES),
  childLastName: sharedLastName ?? randomOf(LAST_NAMES),
  age: Math.floor(Math.random() * 12) + 1,
  pickupPin: randomPin(),
  parentName: `${randomOf(FIRST_NAMES)} ${sharedLastName ?? randomOf(LAST_NAMES)}`,
});

const buildRandomJob = () => {
  const kidCount = Math.floor(Math.random() * 4) + 1; // 1-4 children
  const sharedLastName = kidCount > 1 ? randomOf(LAST_NAMES) : undefined;
  const children = Array.from({ length: kidCount }, () =>
    randomChild(sharedLastName)
  );

  const date = new Date();
  const dateStr = `${(date.getMonth() + 1).toString().padStart(2, '0')}-${date
    .getDate()
    .toString()
    .padStart(2, '0')}-${date.getFullYear()}`;

  return {
    familyId: `load-test-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    children,
    requestedAt: serverTimestamp(),
    requestedBy: LOAD_TEST_TAG,
    date: dateStr,
    status: 'pending' as const,
  };
};

const statusColor: Record<JobStatus, 'default' | 'warning' | 'success' | 'error'> = {
  pending: 'default',
  processing: 'warning',
  printed: 'success',
  error: 'error',
};

const fmtTime = (t?: { toDate: () => Date } | null) =>
  t ? t.toDate().toLocaleTimeString() : '—';

const PrintQueueLoadTest: React.FC = () => {
  const { userData, loading: authLoading, isChildcareAdmin } = useAuth();
  const [entries, setEntries] = useState<QueueEntry[]>([]);
  const [busyBurst, setBusyBurst] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [lastError, setLastError] = useState('');

  useEffect(() => {
    if (!userData || !isChildcareAdmin) return;
    const q = query(
      collection(db, 'printQueue'),
      where('requestedBy', '==', LOAD_TEST_TAG)
    );
    const unsubscribe = onSnapshot(q, (snap) => {
      const rows = snap.docs.map(
        (d) => ({ id: d.id, ...d.data() } as QueueEntry)
      );
      rows.sort(
        (a, b) =>
          (b.requestedAt?.toDate().getTime() ?? 0) -
          (a.requestedAt?.toDate().getTime() ?? 0)
      );
      setEntries(rows);
    });
    return () => unsubscribe();
  }, [userData, isChildcareAdmin]);

  const counts = useMemo(() => {
    const c: Record<JobStatus, number> = {
      pending: 0,
      processing: 0,
      printed: 0,
      error: 0,
    };
    entries.forEach((e) => {
      if (c[e.status] !== undefined) c[e.status]++;
    });
    return c;
  }, [entries]);

  const addJob = async () => {
    try {
      await addDoc(collection(db, 'printQueue'), buildRandomJob());
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Failed to add job');
    }
  };

  const burst = async (count: number) => {
    setBusyBurst(true);
    setLastError('');
    try {
      for (let i = 0; i < count; i++) {
        // eslint-disable-next-line no-await-in-loop
        await addDoc(collection(db, 'printQueue'), buildRandomJob());
      }
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Burst failed');
    } finally {
      setBusyBurst(false);
    }
  };

  const clearTestJobs = async () => {
    setClearing(true);
    setLastError('');
    try {
      await Promise.all(
        entries.map((e) => deleteDoc(doc(db, 'printQueue', e.id)))
      );
    } catch (err) {
      setLastError(err instanceof Error ? err.message : 'Failed to clear jobs');
    } finally {
      setClearing(false);
    }
  };

  if (authLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!userData) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" textAlign="center" gutterBottom>
          Admin Sign In
        </Typography>
        <LoginPage onUserSet={Function.prototype as () => void} />
      </Container>
    );
  }

  if (!isChildcareAdmin) {
    return (
      <Container maxWidth="sm" sx={{ mt: 6 }}>
        <Typography variant="h6" color="error" textAlign="center">
          Access Denied
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mt: 1 }}
        >
          You need the childcare role to view this page.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>
        Print Queue Load Test
      </Typography>
      <Alert severity="warning" sx={{ mb: 3 }}>
        This writes directly to the real <code>printQueue</code> collection.
        If a print server is connected, these jobs will print for real. Use
        the clear button below to remove test jobs when you&apos;re done.
      </Alert>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            onClick={addJob}
          >
            Add 1 Job
          </Button>
          <Button
            variant="outlined"
            onClick={() => burst(5)}
            disabled={busyBurst}
          >
            Burst x5
          </Button>
          <Button
            variant="outlined"
            onClick={() => burst(10)}
            disabled={busyBurst}
          >
            Burst x10
          </Button>
          <Button
            variant="outlined"
            onClick={() => burst(25)}
            disabled={busyBurst}
          >
            Burst x25
          </Button>
          <Button
            variant="outlined"
            onClick={() => burst(50)}
            disabled={busyBurst}
            startIcon={busyBurst ? <CircularProgress size={16} /> : undefined}
          >
            Burst x50
          </Button>
          <Box flexGrow={1} />
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={clearTestJobs}
            disabled={clearing || entries.length === 0}
          >
            Clear Test Jobs ({entries.length})
          </Button>
        </Stack>
        {lastError && (
          <Typography color="error" variant="body2" sx={{ mt: 1 }}>
            {lastError}
          </Typography>
        )}
      </Paper>

      <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip label={`Pending: ${counts.pending}`} color="default" />
        <Chip label={`Processing: ${counts.processing}`} color="warning" />
        <Chip label={`Printed: ${counts.printed}`} color="success" />
        <Chip label={`Error: ${counts.error}`} color="error" />
        <Chip label={`Total: ${entries.length}`} variant="outlined" />
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Children</TableCell>
              <TableCell>Requested</TableCell>
              <TableCell>Claimed</TableCell>
              <TableCell>Printed</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {entries.map((e) => (
              <TableRow key={e.id} hover>
                <TableCell>
                  <Chip
                    size="small"
                    label={e.status}
                    color={statusColor[e.status] ?? 'default'}
                  />
                </TableCell>
                <TableCell>
                  {e.children
                    ?.map((c) => `${c.childFirstName} ${c.childLastName}`)
                    .join(', ')}
                </TableCell>
                <TableCell>{fmtTime(e.requestedAt)}</TableCell>
                <TableCell>{fmtTime(e.claimedAt)}</TableCell>
                <TableCell>{fmtTime(e.printedAt)}</TableCell>
                <TableCell>
                  {e.error && (
                    <Typography variant="caption" color="error">
                      {e.error}
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {entries.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No test jobs yet. Click &quot;Add 1 Job&quot; to start.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
};

export default PrintQueueLoadTest;
